import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthService } from "../services/authService";
import { getThemeAssets } from "../utils/theme";

const LoginEmail: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const themeAssets = getThemeAssets();

  const bannerData = [
    {
      image: "/assets/banners/1.jpg",
      title: "Never Miss a Lead Again",
      subtitle: "Lead Management",
      description:
        "Track and nurture your leads from one place. Maximize your lead conversion by transforming prospects into loyal customers with intelligent tracking and automated workflows.",
    },
    {
      image: "/assets/banners/2.jpg",
      title: "Visual Pipeline Management",
      subtitle: "Sales Pipeline",
      description:
        "Manage your sales pipeline with intuitive drag-and-drop boards. Track deals through every stage and accelerate your sales cycle with real-time visibility.",
    },
    {
      image: "/assets/banners/3.jpg",
      title: "Streamline Your Sales Process",
      subtitle: "Pipeline Management",
      description:
        "Visualize and optimize your entire sales pipeline from first contact to closed deal. Ensure no opportunity falls through the cracks.",
    },
    {
      image: "/assets/banners/4.jpg",
      title: "Enterprise-Grade Security",
      subtitle: "Secure & Reliable",
      description:
        "Your data is protected with industry-leading security measures, encrypted connections, and role-based access controls.",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerData.length]);

  useEffect(() => {
    const errorParam = searchParams.get("error");
    const emailParam = searchParams.get("email");
    if (errorParam) {
      let errorMessage = decodeURIComponent(errorParam);
      if (emailParam) errorMessage += ` (${decodeURIComponent(emailParam)})`;
      setError(errorMessage);
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("error");
      newUrl.searchParams.delete("email");
      window.history.replaceState({}, document.title, newUrl.pathname);
    }
  }, [searchParams]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
      const response = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Login failed");
      if (data.status === "success" && data.data?.token) {
        AuthService.setToken(data.data.token, { expires: rememberMe ? 30 : 7 });
        AuthService.setUserData(data.data.user);
        localStorage.setItem("crm_user", JSON.stringify(data.data.user));
        const clientId = data.data.user?.client_id_fk;
        navigate(clientId === 9 || clientId === "9" ? "/leads_admin" : "/leads");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left Side - Image Carousel */}
      <div className="w-full h-[100vh] lg:w-1/2 lg:h-screen relative overflow-hidden bg-gray-900">
        {bannerData.map((banner, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
              <div className="max-w-2xl">
                <p className="text-sm font-medium text-yellow-400 mb-2 uppercase tracking-wider">
                  {banner.subtitle}
                </p>
                <h2 className="text-4xl font-bold mb-4 leading-tight">{banner.title}</h2>
                <p className="text-base text-gray-200 leading-relaxed">{banner.description}</p>
              </div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {bannerData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? "bg-white w-8" : "bg-white/50 hover:bg-white/75"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Right Side - Email Login Form */}
      <div className="w-full lg:w-1/2 min-h-screen lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0">
        <div className="w-full max-w-md mx-auto lg:mx-8">
          <div className="text-center mb-6">
            <img src={themeAssets.loginLogo} alt={themeAssets.brandName} className="h-10 sm:h-12 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Sign in with Email</h1>
            <p className="text-sm text-gray-500">Enter your email address and password</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent transition-all duration-200 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? "👁️" : "🔒"}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-brandColor hover:text-brand-hover"
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brandColor text-white py-3 px-4 rounded-lg font-medium hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
            >
              {isLoading ? "Signing In..." : "Sign In with Email"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              ← Back to Login
            </button>
          </div>

          <p className="mt-4 text-center text-xs text-gray-400">
            By continuing, you agree to our{" "}
            <a href="#" className="text-blue-500 hover:text-blue-600">Terms of Service</a>{" "}
            and{" "}
            <a href="#" className="text-blue-500 hover:text-blue-600">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginEmail;
